export interface NamedAddressSuggestion {
    label: string;
    countryCode: string;
    region: string;
    city: string;
    line1: string;
    postalCode?: string;
    rawAddress: string;
    locality?: string;
    district?: string;
    placeName?: string;
    longitude?: number;
    latitude?: number;
}

interface PhotonProperties {
    type?: string;
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    locality?: string;
    district?: string;
    county?: string;
    state?: string;
    country?: string;
    countrycode?: string;
}

const PHOTON_ENDPOINT = 'https://photon.komoot.io/api/';

export function photonLanguage(locale?: string): string {
    return locale?.trim().toLowerCase() === 'ro' ? 'default' : 'en';
}
const COUNTRY_SEARCH_BOUNDS: Record<string, string> = {
    RO: '20.2,43.5,29.8,48.4',
};

export function mapPhotonFeature(
    properties: PhotonProperties = {},
    geometry?: { type?: string; coordinates?: number[] },
    options?: { requireStreet?: boolean },
): NamedAddressSuggestion {
    const countryCode = (properties.countrycode || '').trim().toUpperCase();
    const region = (properties.state || properties.county || '').trim();
    const locality = properties.locality?.trim();
    const district = properties.district?.trim();
    const placeName = properties.name?.trim();
    const city = (properties.city || locality || district || '').trim();
    const street = properties.street?.trim() || (options?.requireStreet && properties.type === 'street' ? placeName : '');
    const streetLine = [street, properties.housenumber].map((value) => value?.trim()).filter(Boolean).join(' ');
    // A place name is a street only when Photon identifies a street. Broad searches retain the place fallback.
    const line1 = options?.requireStreet ? (street ? streetLine : '') : streetLine || placeName || '';
    const label = [line1 || placeName, city, region, properties.country].map((value) => value?.trim()).filter(Boolean).join(', ');
    const postalCode = properties.postcode?.trim();
    const [longitude, latitude] = geometry?.type === 'Point' && geometry.coordinates?.length === 2
        ? geometry.coordinates
        : [];
    return {
        label: label || line1 || city || countryCode,
        countryCode,
        region,
        city,
        line1,
        ...(postalCode ? { postalCode } : {}),
        rawAddress: label || line1,
        ...(locality ? { locality } : {}),
        ...(district ? { district } : {}),
        ...(placeName ? { placeName } : {}),
        ...(typeof longitude === 'number' && typeof latitude === 'number' ? { longitude, latitude } : {}),
    };
}

export async function searchPhotonAddresses(
    query: string,
    signal?: AbortSignal,
    countryCode?: string,
    locale?: string,
    options?: { requireStreet?: boolean },
): Promise<NamedAddressSuggestion[]> {
    const normalizedCountry = countryCode?.trim().toUpperCase();
    // Enhanced forms opt in. Callers without options retain their released broad-search behavior.
    const requireStreet = options?.requireStreet === true;
    const url = new URL(PHOTON_ENDPOINT);
    url.searchParams.set('q', query);
    url.searchParams.set('limit', '15');
    url.searchParams.set('lang', photonLanguage(locale));
    if (requireStreet) {
        url.searchParams.append('layer', 'street');
        url.searchParams.append('layer', 'house');
    }
    const bounds = normalizedCountry ? COUNTRY_SEARCH_BOUNDS[normalizedCountry] : undefined;
    if (bounds) {
        url.searchParams.set('bbox', bounds);
    }
    const response = await fetch(url.toString(), { signal });
    if (!response.ok) {
        throw new Error(`Address search failed (${response.status})`);
    }
    const body = await response.json() as {
        features?: Array<{ properties?: PhotonProperties; geometry?: { type?: string; coordinates?: number[] } }>;
    };
    const seen = new Set<string>();
    return (body.features || []).filter(feature => !requireStreet || !feature.properties?.type
        || ['street', 'house'].includes(feature.properties.type))
        .map(feature => mapPhotonFeature(feature.properties, feature.geometry, { requireStreet })).filter((suggestion) => {
        if (requireStreet && !suggestion.line1.trim()) return false;
        if (normalizedCountry && suggestion.countryCode !== normalizedCountry) {
            return false;
        }
        const key = suggestion.label.trim().toLowerCase();
        if (!key || seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    }).slice(0, 5);
}
